'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  ContactAttribute,
  ContactAttributeList,
} from '@eliadmin/core/src/components/contact/contact-attribute-list';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug ?? '#'} className="flex-1" prefetch={false}>
          <SheetClose
            className={cn(
              'text-left font-semibold',
              layer === 1 && 'text-sm font-medium',
              layer && layer > 1 && 'text-xs font-normal',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger aria-label={category.name} className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const { t } = useTranslations();
  const menuLabel = t('text3241')?.trim() && !/^text\d+$/.test(t('text3241').trim()) ? t('text3241') : 'Menu';

  const categories = props.categories;

  console.log(categories);

  return (
    <>
      <Sheet>
        <SheetTrigger aria-label={menuLabel}
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          {HEADER_CONFIG?.sidebar?.triggerIcon}
        </SheetTrigger>
        <SheetContent
          side={props.side}
          className={cn(
            'justify-between overflow-auto p-0',
            'ea-mobile-sidebar__content',
            {
              'pt-20': !props?.firstSeparator,
            },
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'right-4 top-4',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <SheetTitle className="sr-only">{menuLabel}</SheetTitle>
          <div className=" px-3">
            <ul
              className={cn(
                'mb-4 overflow-y-auto',
                'ea-menu-sidebar__list',
                // className?.list,
              )}
            >
              <Accordion type="single" collapsible>
                {categories?.map((category, index) => (
                  <CategoryItem key={index} category={category} />
                ))}
              </Accordion>
            </ul>
            {!!props.menu && (
              <div className="flex flex-col gap-2 p-4">
                <Separator />
                {props.menu?.map((item, index) => {
                  const Comp = item.path
                    ? isRelativeHref(getSafeHref(item.path))
                      ? Link
                      : 'a'
                    : 'span';
                  return (
                    <SheetClose key={index} asChild>
                      <Comp
                        href={item.path ? getSafeHref(item.path) : (undefined as any)}
                        className="flex w-full"
                        prefetch={false}
                      >
                        <div className={cn('text-sm', '')}>{item.name}</div>
                      </Comp>
                    </SheetClose>
                  );
                })}
              </div>
            )}
            <div className="p-4">
              <Separator className="mb-3" />
              <ContactAttributeList
                className={cn('font-bold', 'ea-contact-page__phone-email-wrapper')}
              >
                <div className="text-sm font-normal">
                  <span className="font-bold">{t('text611')}</span> {t('text612')}
                </div>
                {generalLayout.contact?.phone && (
                  <ContactAttribute
                    className={{ text: 'text-sm text-primary', icon: 'text-primary' }}
                    icon={CONTACT_CONFIG.phoneIcon}
                    text={
                      <a href={`tel:${generalLayout.contact?.phone}`}>
                        {generalLayout.contact?.phone}
                      </a>
                    }
                  />
                )}
                {generalLayout.contact?.email && (
                  <ContactAttribute
                    className={{ text: 'text-sm text-primary', icon: 'text-primary' }}
                    icon={CONTACT_CONFIG.mailIcon}
                    text={
                      <a href={`mailto:${generalLayout.contact?.email}`}>
                        {generalLayout.contact?.email}
                      </a>
                    }
                  />
                )}
              </ContactAttributeList>
            </div>
            <div className="p-4">
              <Separator className="mb-3" />
              <SocialIcons className={{ icon: 'h-5 w-5', button: 'h-12 w-12', wrapper: '' }} />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
